<template>
	<textarea
		ref="textareaRef"
		class="kk-textarea"
		:class="[
			!isResizeSmart && 'kk-textarea--draggable',
			isDisabled && 'kk-textarea--disabled',
			hasError && !isDisabled && 'kk-textarea--error',
		]"
		:disabled="isDisabled"
		:placeholder="placeholder"
		:value="textareaValue"
		:maxlength="maxlength"
		:rows="+rows"
		v-bind="$attrs"
		:style="{
			'border-width': borderWidth + 'px',
		}"
		@input="handleInput"
	></textarea>
</template>

<script>
import { computed, onMounted, ref, unref, watch } from 'vue';
import { useNullString } from '../../hooks/useNullString';
import { useModel } from '../../hooks/useModel';

export const RESIZE_TYPE = Object.freeze({
	VERTICAL: 'vertical',
	SMART: 'smart',
});

export default {
	name: 'KkTextarea',
	props: {
		resize: {
			type: String,
			default: RESIZE_TYPE.VERTICAL,
			validator: (type) => Object.values(RESIZE_TYPE).indexOf(type) !== -1,
		},
		placeholder: {
			type: String,
			default: '',
		},
		isDisabled: {
			type: Boolean,
			default: false,
		},
		hasError: {
			type: Boolean,
			default: false,
		},
		// for v-model in vue2
		value: {
			type: String,
			default: '',
		},
		// for v-model in vue3
		modelValue: {
			type: String,
			default: '',
		},
		maxlength: {
			type: [String, Number],
			default: null,
		},
		rows: {
			type: [String, Number],
			default: 4,
			validator: (val) => Number(val) > 0,
		},
	},
	emits: [
		'input', // for v-model in vue2
		'update:modelValue', // for v-model in vue3
	],
	setup(props, { emit }) {
		const borderWidth = ref(1);
		const textareaRef = ref(null);
		const { model: modelValue } = useModel(props, emit);
		const { value: textareaValue, handleInput } = useNullString(modelValue);

		const isResizeSmart = computed(() => {
			return props.resize === RESIZE_TYPE.SMART;
		});

		function resetAreaHeight() {
			// 參考 https://stackoverflow.com/questions/17772260/textarea-auto-height
			const el = unref(textareaRef);
			if (!el) {
				return;
			}

			if (isResizeSmart.value) {
				el.style.height = 'auto'; // reset textarea scrollHeight
				const heightWithBorder = el.scrollHeight + 2 * borderWidth.value;
				el.style.height = `${heightWithBorder}px`;
			}
		}

		watch(() => [isResizeSmart.value, props.rows], resetAreaHeight);

		onMounted(() => {
			// 需要等 dom ready 後才能正確計算元素高度
			if (props.value) {
				resetAreaHeight();
			}
		});

		return {
			textareaRef,
			isResizeSmart,
			textareaValue,
			handleInput,
			borderWidth,
		};
	},
};
</script>
